/**
 * Construct a download URL for a CMS content row.
 *
 * The backend serves a fully-qualified `url` field on each row; this is the
 * fast path. Older rows (and rows from APIs that haven't yet adopted the
 * field) need a fallback synthesized from blob storage + content id +
 * extension.
 *
 * Files are stored at `<contentId>.<ext>` where `<ext>` matches the
 * extension of the originally uploaded `name`. Falls back to `.zip` for
 * legacy rows that omit the extension entirely.
 */
import type { CmsContentInput } from './types';

export function contentDownloadUrl(blobBaseUrl: string, content: CmsContentInput): string {
    if (content.url) return content.url;
    if (!content.id) {
        throw new Error('[contentDownloadUrl] cannot synthesize URL: content has neither url nor id');
    }
    const ext = extractExtension(content.name) ?? 'zip';
    const base = blobBaseUrl.replace(/\/$/, '');
    return `${base}/scene-content-files/${content.id}.${ext}`;
}

/** Extract the file extension (without dot) from a filename like "scene.tracex". */
function extractExtension(name: string | null | undefined): string | null {
    if (!name) return null;
    const dot = name.lastIndexOf('.');
    if (dot < 0 || dot === name.length - 1) return null;
    return name.slice(dot + 1).toLowerCase();
}

/** Display name with fallback when `name` is missing. */
export function contentDisplayName(content: CmsContentInput): string {
    return content.name ?? `Content (${content.contentType ?? 'unknown'})`;
}

/**
 * Resolve a Scene's image-marker URL. Joins `blobBaseUrl` with `imageUrl`,
 * tolerating either-or-neither trailing/leading slashes.
 */
export function resolveImageAnchorUrl(blobBaseUrl: string, imageUrl: string): string {
    return blobBaseUrl.replace(/\/$/, '') + '/' + imageUrl.replace(/^\//, '');
}
